import { api } from './api';

export interface TransactionGridItem {
  id: number;
  number: string;
  created_date: string;
  status: 'new' | 'payed' | 'approved' | 'canceled';
  type: 'prepayment' | 'invoice';
  total: number;
  company_id?: number;
  company_name?: string;
  agreement_number?: string;
}

export interface TransactionGridPage {
  items: TransactionGridItem[];
  total: number;
}

export interface TransactionListParams {
  skip?: number;
  limit?: number;
  search?: string;
  type?: 'prepayment' | 'invoice';
  status?: 'new' | 'payed' | 'approved' | 'canceled';
  createdFrom?: string;
  createdTo?: string;
}

export interface TransactionItem {
  id: number;
  transaction_id: number;
  agreement_to_service_rel_id: number;
  quantity: number;
  discount: number;
  total_price: number;
  agreement_to_service_rel?: {
    id: number;
    agreement_id: number;
    service_id: number;
    price: number;
    rel_type: string;
    service?: {
      id: number;
      name: string;
      identifier?: string;
    };
  };
  // Для обратной совместимости
  service_id?: number;
  service?: {
    id: number;
    name: string;
    identifier?: string;
  };
}

export interface TransactionWithDetails {
  id: number;
  number: string;
  total: number;
  status: 'new' | 'payed' | 'approved' | 'canceled';
  confirmation_document_url?: string;
  confirmation_document_file?: string;
  type: 'prepayment' | 'invoice';
  company_id?: number;
  agreement_id?: number;
  field1?: string; // comment
  field2?: string;
  field3?: string;
  field4?: string;
  field5?: string;
  created_date: string;
  updated_date?: string;
  canceled_date?: string;
  company_name?: string;
  agreement_number?: string;
  comment?: string;
  transaction_items?: TransactionItem[];
}

export interface TransactionService {
  id: number;
  service_id: number;
  service_name: string;
  price: number;
  rel_type: 'per_year' | 'per_month' | 'one_time';
  unit: string;
  description?: string;
}

export interface TransactionCreate {
  total?: number;
  number?: string;  // Optional - will be auto-generated if not provided
  status?: 'new' | 'payed' | 'approved' | 'canceled';
  confirmation_document_url?: string;
  type: 'prepayment' | 'invoice';
  company_id?: number;
  agreement_id?: number;
  field1?: string; // comment
  field2?: string;
  field3?: string;
  field4?: string;
  field5?: string;
  send_email?: boolean;
  transaction_items?: TransactionItemCreate[];
}

export interface TransactionItemCreate {
  agreement_to_service_rel_id: number;
  quantity?: number;
  discount?: number;   // скидка для invoice
  total_price?: number; // для prepayment — итоговая сумма с фронта/API
}

export interface TransactionUpdate {
  total?: number;
  number?: string;
  status?: 'new' | 'payed' | 'approved' | 'canceled';
  confirmation_document_url?: string;
  type?: 'prepayment' | 'invoice';
  company_id?: number;
  agreement_id?: number;
  canceled_date?: string;
  field1?: string; // comment
  field2?: string;
  field3?: string;
  field4?: string;
  field5?: string;
  transaction_items?: TransactionItemCreate[];
}

export interface TransactionReportRow {
  modification_date: string | null;
  created_date: string | null;
  company_name: string | null;
  transaction_number: string;
  service_name: string | null;
  payment_amount: number;
  unit_price: number | null;
  quantity: number;
  discount: number;
  payment_comment: string;
  manager_name: string | null;
  status: string;
}

export interface TransactionReportResponse {
  rows: TransactionReportRow[];
}

export interface TransactionReportParams {
  expandLines: boolean;
  createdFrom?: string;
  createdTo?: string;
  updatedFrom?: string;
  updatedTo?: string;
}

export const transactionsApi = {
  getReport: async (params: TransactionReportParams): Promise<TransactionReportResponse> => {
    const searchParams = new URLSearchParams();
    searchParams.set('expand_lines', params.expandLines ? 'true' : 'false');
    if (params.createdFrom) searchParams.set('created_from', params.createdFrom);
    if (params.createdTo) searchParams.set('created_to', params.createdTo);
    if (params.updatedFrom) searchParams.set('updated_from', params.updatedFrom);
    if (params.updatedTo) searchParams.set('updated_to', params.updatedTo);
    const response = await api.get(`/api/transactions/report?${searchParams.toString()}`);
    return response.data;
  },

  // Получить список транзакций для грида (с пагинацией)
  getTransactions: async (params: TransactionListParams = {}): Promise<TransactionGridPage> => {
    const searchParams = new URLSearchParams();
    if (params.skip != null) searchParams.set('skip', String(params.skip));
    if (params.limit != null) searchParams.set('limit', String(params.limit));
    if (params.search) searchParams.set('search', params.search);
    if (params.type) searchParams.set('type', params.type);
    if (params.status) searchParams.set('status', params.status);
    if (params.createdFrom) searchParams.set('created_from', params.createdFrom);
    if (params.createdTo) searchParams.set('created_to', params.createdTo);
    const qs = searchParams.toString();
    const response = await api.get(`/api/transactions${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  // Получить транзакцию по ID с деталями
  getTransaction: async (id: number): Promise<TransactionWithDetails> => {
    const response = await api.get(`/api/transactions/${id}`);
    return response.data;
  },

  // Создать новую транзакцию
  createTransaction: async (transaction: TransactionCreate): Promise<TransactionWithDetails> => {
    const response = await api.post('/api/transactions', transaction);
    return response.data;
  },

  // Обновить транзакцию
  updateTransaction: async (id: number, transaction: TransactionUpdate): Promise<TransactionWithDetails> => {
    const response = await api.put(`/api/transactions/${id}`, transaction);
    return response.data;
  },

  // Получить услуги для транзакции
  getTransactionServices: async (id: number): Promise<TransactionService[]> => {
    const response = await api.get(`/api/transactions/${id}/services`);
    return response.data;
  },

  // Добавить услугу к транзакции
  addServiceToTransaction: async (transactionId: number, agreementToServiceRelId: number): Promise<void> => {
    await api.post(`/api/transactions/${transactionId}/services`, {
      agreement_to_service_rel_id: agreementToServiceRelId
    });
  },

  // Удалить услугу из транзакции
  removeServiceFromTransaction: async (transactionId: number, billServiceId: number): Promise<void> => {
    await api.delete(`/api/transactions/${transactionId}/services/${billServiceId}`);
  },

  // Скачать PDF счета
  downloadTransactionPdf: async (transactionId: number): Promise<void> => {
    const response = await api.get(`/api/transactions/${transactionId}/download-pdf`, {
      responseType: 'blob'
    });
    
    // Создаем ссылку для скачивания
    const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = url;
    
    // Получаем имя файла из заголовков или используем дефолтное
    const contentDisposition = response.headers['content-disposition'];
    let filename = `bill_${transactionId}.pdf`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1];
      }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Загрузить файл подтверждения для транзакции
  uploadConfirmationFile: async (transactionId: number, file: File): Promise<{ message: string; file_path: string; file_name: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/api/transactions/${transactionId}/upload-confirmation`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data'
      }
    });
    return response.data;
  },

  // Скачать файл подтверждения транзакции (index — при нескольких файлах)
  downloadConfirmationFile: async (transactionId: number, index: number = 0, suggestedName?: string): Promise<void> => {
    const response = await api.get(`/api/transactions/${transactionId}/download-confirmation`, {
      params: { index },
      responseType: 'blob'
    });

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let filename = suggestedName ?? `confirmation_${transactionId}`;
    if (contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
      if (filenameMatch) {
        filename = filenameMatch[1].replace(/^["']|["']$/g, '');
      }
    }

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  // Отправить транзакцию на подтверждение (NEW -> PAYED) - только для клиентов
  sendForApproval: async (transactionId: number): Promise<TransactionWithDetails> => {
    const response = await api.post(`/api/transactions/${transactionId}/send-for-approval`);
    return response.data;
  }
};

























